import React from "react";
import { Box, Typography } from "@mui/material";
import { RequireIcon as CustomRequireIcon } from "@ippon/shared-assets";
import { colors } from "../../theme/colors";
import CustomButton from "../../Buttons/Button/CustomButton";
import CustomTextInput from "../TextInput/CustomTextInput";
import CustomSingleSelect from "../Dropdown/SingleSelect/CustomSingleSelect";

export type PrefectureOption = { value: string | number; label: string };

type ReversePostalCodeLookupParams = {
  prefecture: string;
  city: string;
  blockNumber?: string;
};

type ReversePostalCodeLookupResult = {
  postalCode: string;
  isAmbiguous?: boolean;
  candidateCount?: number;
};

export interface PostalCodeAutoAddressInputProps {
  register: any;
  errors: any;
  setValue: any;
  getValues?: any;
  setError?: any;
  clearErrors?: any;
  control?: any;
  disabled?: boolean;
  prefectureOptions: PrefectureOption[];
  // field names
  postalCodeFieldName?: string;
  prefectureFieldName?: string;
  cityFieldName?: string;
  blockNumberFieldName?: string;
  // labels
  postalCodeLabel?: string;
  prefectureLabel?: string;
  cityLabel?: string;
  // layout
  leftInputContainerWidth?: string | number;
  inputWidth?: string | number;
  inputWidthSp?: string | number;
  labelWidth?: string;
  labelWidthSp?: string;
  // validation
  isRequired?: boolean;
  postalCodeRequired?: boolean;
  addressRequired?: boolean;
  // forward search (postal code → address)
  autoSearchEnabled?: boolean;
  showSearchButton?: boolean;
  searchButtonLabel?: string;
  // reverse search (address → postal code)
  reversePostalCodeLookup?: (
    params: ReversePostalCodeLookupParams
  ) => Promise<ReversePostalCodeLookupResult>;
  autoReverseSearchEnabled?: boolean;
  showReverseSearchButton?: boolean;
  reverseSearchButtonLabel?: string;
  onSearchError?: (message: string) => void;
}

const PostalCodeAutoAddressInput: React.FC<PostalCodeAutoAddressInputProps> = ({
  register,
  errors,
  setValue,
  getValues,
  setError,
  clearErrors,
  control,
  disabled = false,
  prefectureOptions,
  postalCodeFieldName = "postal_code",
  prefectureFieldName = "prefecture",
  cityFieldName = "city",
  blockNumberFieldName = "block_number",
  postalCodeLabel = "郵便番号",
  prefectureLabel = "都道府県",
  cityLabel = "市区町村",
  leftInputContainerWidth = "600px",
  inputWidth,
  inputWidthSp,
  labelWidth = "120px",
  labelWidthSp = "47%",
  isRequired = false,
  postalCodeRequired,
  addressRequired,
  autoSearchEnabled = true,
  showSearchButton = false,
  searchButtonLabel = "住所を検索",
  reversePostalCodeLookup,
  autoReverseSearchEnabled = true,
  showReverseSearchButton = false,
  reverseSearchButtonLabel = "郵便番号を検索",
  onSearchError,
}) => {
  const resolvedPostalCodeRequired = postalCodeRequired ?? isRequired;
  const resolvedAddressRequired = addressRequired ?? isRequired;
  const resolvedInputWidth = String(inputWidth ?? leftInputContainerWidth);
  const resolvedInputWidthSp = String(inputWidthSp ?? "100%");

  const [isReverseSearching, setIsReverseSearching] = React.useState(false);
  const [prefectureReverseError, setPrefectureReverseError] = React.useState("");

  const [prefOptionsInternal, setPrefOptionsInternal] = React.useState<
    PrefectureOption[]
  >(Array.isArray(prefectureOptions) ? prefectureOptions : []);

  React.useEffect(() => {
    setPrefOptionsInternal(
      Array.isArray(prefectureOptions) ? prefectureOptions : []
    );
  }, [prefectureOptions]);

  // ---------- helpers ----------

  const setManualFieldError = (fieldName: string, message: string) => {
    if (setError) {
      setError(fieldName, { type: "manual", message });
    } else if (onSearchError) {
      onSearchError(message);
    }
  };

  const showSearchError = (message: string) => {
    setManualFieldError(postalCodeFieldName, message);
  };

  const clearFieldErrors = (fields: string | string[]) => {
    if (clearErrors) {
      clearErrors(fields);
      return;
    }
    if (typeof fields === "string" && setError) {
      setError(fields, { type: "manual", message: "" });
    }
  };

  const resolveReverseSearchErrorMessage = (error: unknown): string => {
    if (typeof error === "object" && error !== null) {
      const e = error as { status?: unknown; message?: unknown };
      const status = typeof e.status === "number" ? e.status : undefined;
      const message = typeof e.message === "string" ? e.message : "";
      if (status === 404) return "該当する郵便番号が見つかりません";
      if (status === 500) return "郵便番号検索に失敗しました";
      if (message) return message;
    }
    return "郵便番号検索に失敗しました";
  };

  // ---------- forward search (postal code → address) ----------

  const searchAddressByPostalCode = async (postalCodeInput?: string) => {
    if (disabled) return;
    const value = (postalCodeInput || "").replace(/[^0-9]/g, "");
    setValue(postalCodeFieldName, value);

    if (value.length === 0) {
      showSearchError("郵便番号を入力してください");
      return;
    }
    if (value.length !== 7) {
      showSearchError("郵便番号は7桁で入力してください");
      return;
    }

    try {
      const response = await fetch(
        `https://zipcloud.ibsnet.co.jp/api/search?zipcode=${value}`
      );
      const data = await response.json();
      if (data && data.results && data.results.length > 0) {
        const result = data.results[0];
        let prefectureValue =
          prefOptionsInternal.find((p) => p.label === result.address1)?.value ?? "";

        if (!prefectureValue) {
          const newOption = {
            label: result.address1,
            value: result.address1,
          } as PrefectureOption;
          setPrefOptionsInternal((prev) => {
            const exists = prev.some(
              (o) =>
                String(o.value) === String(newOption.value) ||
                o.label === newOption.label
            );
            return exists ? prev : [...prev, newOption];
          });
          prefectureValue = newOption.value;
        }
        const cityValue = result.address2 + result.address3;

        clearFieldErrors([postalCodeFieldName, prefectureFieldName, cityFieldName]);
        setPrefectureReverseError("");
        setValue(postalCodeFieldName, value, { shouldDirty: true, shouldValidate: true, shouldTouch: true });
        setValue(prefectureFieldName, prefectureValue, { shouldDirty: true, shouldValidate: true, shouldTouch: true });
        setValue(cityFieldName, cityValue, { shouldDirty: true, shouldValidate: true, shouldTouch: true });
        clearFieldErrors(postalCodeFieldName);
      } else {
        showSearchError("該当する住所が見つかりません");
      }
    } catch {
      showSearchError("該当する住所が見つかりません");
    }
  };

  const autoSetAddress = async (
    event: React.ChangeEvent<HTMLInputElement> | React.FocusEvent<HTMLInputElement>
  ) => {
    if (!autoSearchEnabled) return;
    await searchAddressByPostalCode(event.target.value);
  };

  const handleSearchButtonClick = async () => {
    const currentPostalCode = getValues ? getValues(postalCodeFieldName) : undefined;
    await searchAddressByPostalCode(currentPostalCode);
  };

  // ---------- reverse search (address → postal code) ----------

  const doReverseSearch = async () => {
    if (disabled || isReverseSearching || !getValues || !reversePostalCodeLookup) return;

    const prefectureValue = String(getValues(prefectureFieldName) ?? "").trim();
    const cityValue = String(getValues(cityFieldName) ?? "").trim();
    const blockNumberValue = String(getValues(blockNumberFieldName) ?? "").trim();

    if (!prefectureValue || !cityValue) return;

    try {
      setIsReverseSearching(true);
      const result = await reversePostalCodeLookup({
        prefecture: prefectureValue,
        city: cityValue,
        blockNumber: blockNumberValue || undefined,
      });

      if (result.isAmbiguous) {
        setManualFieldError(cityFieldName, "候補が複数あるため、市区町村・番地を詳しく入力してください");
        return;
      }

      const postalCodeValue = String(result.postalCode ?? "").replace(/[^0-9]/g, "");
      if (!postalCodeValue || postalCodeValue.length !== 7) {
        setManualFieldError(postalCodeFieldName, "該当する郵便番号が見つかりません");
        return;
      }

      clearFieldErrors([postalCodeFieldName, blockNumberFieldName]);
      setValue(postalCodeFieldName, postalCodeValue, { shouldDirty: true, shouldValidate: true });
    } catch (error) {
      setManualFieldError(postalCodeFieldName, resolveReverseSearchErrorMessage(error));
    } finally {
      setIsReverseSearching(false);
    }
  };

  const autoSetPostalCode = async (
    event: React.FocusEvent<HTMLElement>
  ) => {
    if (!autoReverseSearchEnabled || !reversePostalCodeLookup) return;
    const cityValue = String((event.target as HTMLInputElement).value ?? "").trim();
    if (!cityValue) return;
    const prefectureValue = String(getValues ? getValues(prefectureFieldName) : "").trim();
    if (!prefectureValue) return;
    await doReverseSearch();
  };

  const handleReverseSearchButtonClick = async () => {
    const prefectureValue = String(getValues ? getValues(prefectureFieldName) : "").trim();
    const cityValue = String(getValues ? getValues(cityFieldName) : "").trim();

    let hasError = false;

    if (!prefectureValue) {
      setPrefectureReverseError("都道府県を選択してください");
      if (setError) {
        setError(prefectureFieldName, { type: "manual", message: "都道府県を選択してください" });
      }
      hasError = true;
    } else {
      setPrefectureReverseError("");
      if (clearErrors) clearErrors(prefectureFieldName);
    }

    if (!cityValue) {
      setManualFieldError(cityFieldName, "市区町村を入力してください");
      hasError = true;
    }

    if (hasError) return;

    setPrefectureReverseError("");
    await doReverseSearch();
  };

  // ---------- render helpers ----------

  const renderButtonColumn = (
    label: string,
    isReq: boolean,
    buttonLabel: string,
    onClick: () => void,
    isButtonLoading: boolean,
    isButtonDisabled: boolean
  ) => (
    <Box
      sx={{
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        gap: "1px",
        alignSelf: "flex-start",
      }}
    >
      {/* CustomTextInput のラベルと高さを合わせるための不可視スペーサー */}
      <Typography
        className="inputLabel"
        aria-hidden="true"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "4px",
          fontSize: "12px !important",
          fontWeight: 700,
          lineHeight: "18px",
          paddingTop: 0,
          paddingBottom: "4px",
          marginBottom: "1px",
          visibility: "hidden",
          textAlign: "left",
          justifyContent: "flex-start",
        }}
      >
        {label}
        {isReq && <CustomRequireIcon />}
      </Typography>
      <CustomButton
        label={buttonLabel}
        buttonCategory="search"
        onClick={onClick}
        disabled={isButtonDisabled}
        isLoading={isButtonLoading}
        sx={{ mt: { xs: 0, sm: "-3px" } }}
      />
    </Box>
  );

  const showReverseButton = showReverseSearchButton && !!reversePostalCodeLookup;

  return (
    <>
      {/* 郵便番号 + 住所を検索ボタン */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 1,
          width: { sm: showSearchButton ? "max-content" : resolvedInputWidth, xs: resolvedInputWidthSp },
          maxWidth: "100%",
        }}
      >
        <Box
          sx={{
            width: { sm: showSearchButton ? resolvedInputWidth : "100%", xs: "auto" },
            flexGrow: { xs: 1, sm: showSearchButton ? 0 : 1 },
            minWidth: 0,
          }}
        >
          <CustomTextInput
            label={postalCodeLabel}
            name={postalCodeFieldName}
            type="text"
            placeholder="1234567（ハイフンなし）"
            register={register}
            errors={errors}
            isRequired={resolvedPostalCodeRequired}
            inputWidth={resolvedInputWidth}
            inputWidthSp={"100%"}
            autoSetAddress={autoSearchEnabled ? autoSetAddress : undefined}
            labelWidth={labelWidth}
            labelWidthSp={labelWidthSp}
            disabled={disabled}
            inputMode="numeric"
          />
        </Box>
        {showSearchButton &&
          renderButtonColumn(
            postalCodeLabel,
            resolvedPostalCodeRequired,
            searchButtonLabel,
            handleSearchButtonClick,
            false,
            disabled
          )}
      </Box>

      {/* 都道府県 */}
      <CustomSingleSelect
        label={prefectureLabel}
        name={prefectureFieldName}
        control={control}
        errors={errors}
        options={prefOptionsInternal}
        inputWidth={String(inputWidth ?? leftInputContainerWidth)}
        inputWidthSp={String(inputWidthSp ?? "100%")}
        placeholder="選択してください"
        isRequired={resolvedAddressRequired}
        labelWidth={labelWidth}
        labelWidthSp={labelWidthSp}
        disabled={disabled}
        onChangeValue={(val) => {
          if (val) {
            if (clearErrors) clearErrors(prefectureFieldName);
            setPrefectureReverseError("");
          }
        }}
      />
      {prefectureReverseError && (
        <Typography
          sx={{
            fontSize: "12px",
            color: colors.status.error,
            marginTop: "-6px",
            marginLeft: "14px",
          }}
        >
          {prefectureReverseError}
        </Typography>
      )}

      {/* 市区町村 + 郵便番号を検索ボタン */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 1,
          width: { sm: showReverseButton ? "max-content" : resolvedInputWidth, xs: resolvedInputWidthSp },
          maxWidth: "100%",
        }}
      >
        {/* onBlur を Box に付けることで CustomTextInput 内部の name 制限を回避 */}
        <Box
          onBlur={autoReverseSearchEnabled && !!reversePostalCodeLookup ? autoSetPostalCode : undefined}
          onChange={() => { if (clearErrors) clearErrors(cityFieldName); }}
          sx={{
            width: { sm: showReverseButton ? resolvedInputWidth : "100%", xs: "auto" },
            flexGrow: { xs: 1, sm: showReverseButton ? 0 : 1 },
            minWidth: 0,
          }}
        >
          <CustomTextInput
            label={cityLabel}
            name={cityFieldName}
            type="text"
            register={register}
            errors={errors}
            isRequired={resolvedAddressRequired}
            inputWidth={resolvedInputWidth}
            inputWidthSp={"100%"}
            labelWidth={labelWidth}
            labelWidthSp={labelWidthSp}
            disabled={disabled}
          />
        </Box>
        {showReverseButton &&
          renderButtonColumn(
            cityLabel,
            resolvedAddressRequired,
            reverseSearchButtonLabel,
            handleReverseSearchButtonClick,
            isReverseSearching,
            disabled || isReverseSearching
          )}
      </Box>
    </>
  );
};

export default PostalCodeAutoAddressInput;
